import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  ReservationFormParams,
  SubscriptionQuestionParams,
} from "../../store/draftReservation/types";

import { Block, Divider, useBreakpoints } from "@stepapp/ui";

import ErrorMessage from "../ErrorMessage";
import { Grid } from "../Grid";
import { ServiceSceneDescription, ServiceSceneHeader } from "./ServiceSceneHeader";
import SimpleAnswerItem from "./SimpleAnswerItem";

type Props = {
  question: SubscriptionQuestionParams;
};

const PeriodicityQuestions = ({ question }: Props) => {
  const { t } = useTranslation();
  const { md } = useBreakpoints();
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();

  const subscriptionOptionId = watch("subscriptionOptionId");
  const singleAnswer = question?.options?.length === 1;

  const setSubscriptionId = useCallback(
    (id: string) => {
      setValue("subscriptionOptionId", id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  useEffect(() => {
    if (singleAnswer) {
      setSubscriptionId(question?.options?.[0]?.id || "");
    }
  }, [setSubscriptionId, singleAnswer, question?.options?.[0]?.id]);

  const gap = md ? 16 : 8;
  return (
    <Block id="subscription">
      {question && (
        <Block marginTop={1} testID={`question-id-${question.id}`}>
          <ServiceSceneHeader name={t("screens.service.howOften")} isRequired />

          <ServiceSceneDescription description={t("screens.service.howOftenDescription")} />

          <Block marginTop={3}>
            <Grid columns={2} gap={gap}>
              {question?.options?.map(({ id, discount, description, name, isFeatured }) => (
                <SimpleAnswerItem
                  description={`${description}`}
                  id={id}
                  key={id}
                  discount={discount}
                  questionId={question.id}
                  isFeatured={isFeatured}
                  isPeriodicityQuestion
                  setSubscriptionId={setSubscriptionId}
                  selected={subscriptionOptionId === id}
                  title={name}
                />
              ))}
            </Grid>

            <ErrorMessage message={errors?.subscriptionOptionId?.message} />
          </Block>

          <Divider />
        </Block>
      )}
    </Block>
  );
};

export default PeriodicityQuestions;
